import type { ReactNode } from "react";

import type { CircuitRowResponse, EarthFaultLoopResponse } from "../services/earthFaultLoop";
import type {
  DisconnectionTimeOrigin,
  LimitBasis,
  MaximumLoopImpedanceOrigin,
  OperatingCurrentOrigin,
  ResistanceBasis,
  TemperatureBasis,
} from "../services/earthFaultLoopContract";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import {
  LIMIT_BASIS_LABELS,
  rowKind,
  STATUS_LABELS,
  statusHeading,
  ZE_ORIGIN_LABELS,
} from "./EarthFaultLoopResultSummary";
import {
  CIRCUIT_KIND_LABELS,
  DEVICE_KIND_LABELS,
  MCB_TYPE_LABELS,
  OPERATING_CURRENT_BASIS_LABELS,
  SUPPLY_KIND_LABELS,
  SYSTEM_KIND_LABELS,
} from "./earthFaultLoopDraft";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";

interface EarthFaultLoopResultPanelProps {
  result: EarthFaultLoopResponse;
}

/** Carried by every result (D8, D14, D12): what the check is and what it is not. */
export const METHOD_NOTE =
  "The loop impedance is the arithmetic sum of the magnitudes of Ze and the cable impedance " +
  "(conservative). The protective conductor's thermal withstand, the touch voltage and the " +
  "additional protection by RCD are not checked here; the sheet says so on every circuit.";

export const RESULT_WORDS = {
  met: "Met",
  notMet: "Not met",
  notCompared: "Not compared",
} as const;

export const TEMPERATURE_BASIS_LABELS: Record<TemperatureBasis, string> = {
  LINKED_CABLE_INSULATION: "linked cable's insulation maximum",
  DECLARED: "declared",
};

export const RESISTANCE_BASIS_LABELS: Record<ResistanceBasis, string> = {
  RHO_OVER_S: "ρ L / S",
  DECLARED_R20: "declared r20",
};

export const TIME_ORIGIN_LABELS: Record<DisconnectionTimeOrigin, string> = {
  DECLARED: "declared",
  REGISTER_VERIFIED: "CPWD App F Table F-2",
  NOT_ESTABLISHED: "not established",
};

export const IA_ORIGIN_LABELS: Record<OperatingCurrentOrigin, string> = {
  DECLARED: "declared",
  IMPLIED_FROM_MAXIMUM_LOOP_IMPEDANCE: "implied from the maximum Zs",
  NOT_ESTABLISHED: "not established",
};

export const ZS_MAX_ORIGIN_LABELS: Record<MaximumLoopImpedanceOrigin, string> = {
  FROM_OPERATING_CURRENT: "from the declared Ia",
  DECLARED: "declared",
  REGISTER_VERIFIED: "CPWD App F Table F-3",
  NOT_ESTABLISHED: "not established",
};

const ROW_LIMIT_BASIS_LABELS: Record<LimitBasis, string> = LIMIT_BASIS_LABELS;

function resultKey(assured: boolean | null): string {
  if (assured === null) {
    return "not-evaluated";
  }
  return assured ? "pass" : "fail";
}

function resultWord(assured: boolean | null): string {
  if (assured === null) {
    return RESULT_WORDS.notCompared;
  }
  return assured ? RESULT_WORDS.met : RESULT_WORDS.notMet;
}

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null | undefined; unit: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The board: its system, Ze on its stated basis, U0 and the comparison voltage.
function BoardTable({ result }: { result: EarthFaultLoopResponse }) {
  const linked = result.external_impedance_origin === "LINKED_FAULT_RUN";
  const nominal = formatQuantity(result.nominal_line_to_earth_voltage_v ?? null, "V");
  const factor = formatQuantity(result.minimum_voltage_factor);
  return (
    <ValueTable title="Board and external impedance">
      <tr data-row-key="system_kind">
        <th scope="row">System earthing</th>
        <td data-system-kind={result.system_kind}>{SYSTEM_KIND_LABELS[result.system_kind]}</td>
      </tr>
      <tr data-row-key="supply_kind">
        <th scope="row">Supply</th>
        <td>{SUPPLY_KIND_LABELS[result.supply_kind]}</td>
      </tr>
      <tr data-row-key="system_nominal_voltage_v">
        <th scope="row">System nominal voltage Un (V)</th>
        <Quantity value={result.system_nominal_voltage_v} unit="V" />
      </tr>
      <tr data-row-key="line_to_earth_voltage_v">
        <th scope="row">Line-to-earth voltage U0 (V)</th>
        <Quantity value={result.line_to_earth_voltage_v} unit="V" />
      </tr>
      <tr data-row-key="external_impedance_origin">
        <th scope="row">Ze source</th>
        <td data-external-impedance-origin={result.external_impedance_origin}>
          {ZE_ORIGIN_LABELS[result.external_impedance_origin]}
        </td>
      </tr>
      <tr data-row-key="external_loop_impedance_ohm">
        <th scope="row">External loop impedance Ze (Ω)</th>
        <Quantity value={result.external_loop_impedance_ohm} unit="Ω" />
      </tr>
      <tr data-row-key="external_impedance_basis">
        <th scope="row">Ze basis</th>
        <td>{result.external_impedance_basis ?? result.external_impedance_source_text ?? EMPTY_QUANTITY}</td>
      </tr>
      <tr data-row-key="fault_run">
        <th scope="row">Earth-fault run</th>
        <td>
          {linked && result.fault_run_calculation_key !== null
            ? `${result.fault_run_calculation_key} rev ${result.fault_run_revision_number} · bus ${
                result.fault_bus_code
              } · ${result.calculation_case ? CASE_LABELS[result.calculation_case] : ""} · ${
                result.fault_type ? FAULT_TYPE_LABELS[result.fault_type] : ""
              }`
            : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="minimum_voltage_factor">
        <th scope="row">Minimum voltage factor C_min</th>
        <td title={factor.exact ?? undefined} data-voltage-factor-origin={result.voltage_factor_origin}>
          {result.minimum_voltage_factor === null ? "not applied (check at nominal U0)" : factor.display}
        </td>
      </tr>
      <tr data-row-key="comparison_voltage_v">
        <th scope="row">Comparison voltage (V)</th>
        <Quantity value={result.comparison_voltage_v} unit="V" />
      </tr>
      <tr data-row-key="nominal_line_to_earth_voltage_v">
        <th scope="row">Declared nominal Uo (band selection only)</th>
        <td title={nominal.exact ?? undefined}>
          {result.nominal_line_to_earth_voltage_v ? nominal.display : EMPTY_QUANTITY}
        </td>
      </tr>
      <tr data-row-key="limit_basis">
        <th scope="row">Limit basis</th>
        <td data-limit-basis={result.limit_basis}>{LIMIT_BASIS_LABELS[result.limit_basis]}</td>
      </tr>
    </ValueTable>
  );
}

// One line per circuit: Zs, Zs_max, I_f, Ia, the outcome (section 9).
function CircuitsTable({ rows }: { rows: readonly CircuitRowResponse[] }) {
  return (
    <section aria-label="Circuits">
      <h3>Circuits</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Circuit</th>
              <th scope="col">Zs(θ) (Ω)</th>
              <th scope="col">Zs max (Ω)</th>
              <th scope="col">I_f (A)</th>
              <th scope="col">Ia (A)</th>
              <th scope="col">Time (s)</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = resultKey(row.disconnection_assured);
              return (
                <tr key={row.code} data-circuit={row.code} data-circuit-status={row.status}>
                  <th scope="row">{row.code}</th>
                  <Quantity value={row.loop_impedance_ohm} unit="Ω" />
                  <Quantity value={row.maximum_loop_impedance_ohm} unit="Ω" />
                  <Quantity value={row.fault_current_a} unit="A" />
                  <Quantity value={row.operating_current_a} unit="A" />
                  <Quantity value={row.required_disconnection_time_s} unit="s" />
                  <td data-check-result={key}>
                    <span data-check-pill>
                      <StatusIcon tone={checkTone(key)} size={16} />
                      {resultWord(row.disconnection_assured)}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// One circuit in full: the cable, the temperature, the loop, the device and the comparison.
function CircuitDetail({ row }: { row: CircuitRowResponse }) {
  const kind = rowKind(row);
  const title = `Circuit ${row.code} — ${STATUS_LABELS[row.status]}`;
  const device = row.device;
  return (
    <section aria-label={title} data-circuit-detail={row.code} data-circuit-kind={kind}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
              <th scope="col">Basis</th>
            </tr>
          </thead>
          <tbody>
            <tr data-row-key="cable">
              <th scope="row">Cable</th>
              <td>
                {formatQuantity(row.line_conductor_area_mm2, "mm²").display} /{" "}
                {row.protective_conductor_area_mm2 === null
                  ? "PE not established"
                  : formatQuantity(row.protective_conductor_area_mm2, "mm²").display}{" "}
                {row.conductor_material === "COPPER" ? "Cu" : "Al"} ·{" "}
                {formatQuantity(row.route_length_m, "m").display} · {row.parallel_runs}{" "}
                {row.parallel_runs === 1 ? "run" : "runs"} (PE {row.protective_conductor_runs})
              </td>
              <td data-cable-source={row.cable_source}>
                {row.linked_cable_run
                  ? `linked cable run ${row.linked_cable_run.study_code} rev ${row.linked_cable_run.revision_number}`
                  : "typed"}
              </td>
            </tr>
            <tr data-row-key="resistance_20c">
              <th scope="row">R1 / R2 at 20 °C (Ω)</th>
              <td title={`${row.line_resistance_20c_ohm} / ${row.protective_resistance_20c_ohm ?? "—"}`}>
                {formatQuantity(row.line_resistance_20c_ohm).display} /{" "}
                {formatQuantity(row.protective_resistance_20c_ohm).display}
              </td>
              <td>
                {RESISTANCE_BASIS_LABELS[row.line_resistance_basis]} (ρ20{" "}
                {row.resistivity_ohm_mm2_per_m} Ω mm²/m, α {row.temperature_coefficient_per_k} /K)
              </td>
            </tr>
            <tr data-row-key="temperature">
              <th scope="row">Conductor temperature θ (°C) · factor</th>
              <td title={row.temperature_factor ?? undefined}>
                {row.conductor_temperature_c === null
                  ? "not declared"
                  : `${formatQuantity(row.conductor_temperature_c, "°C").display} · ${formatQuantity(row.temperature_factor).display}`}
              </td>
              <td>{row.temperature_basis ? TEMPERATURE_BASIS_LABELS[row.temperature_basis] : EMPTY_QUANTITY}</td>
            </tr>
            <tr data-row-key="resistance_theta">
              <th scope="row">R1 / R2 at θ (Ω)</th>
              <td title={`${row.line_resistance_ohm ?? "—"} / ${row.protective_resistance_ohm ?? "—"}`}>
                {formatQuantity(row.line_resistance_ohm).display} /{" "}
                {formatQuantity(row.protective_resistance_ohm).display}
              </td>
              <td>R(θ) = R20 (1 + α (θ − 20))</td>
            </tr>
            <tr data-row-key="reactance">
              <th scope="row">Loop reactance X1 + X2 (Ω)</th>
              <Quantity value={row.loop_reactance_ohm} unit="Ω" />
              <td>
                {row.reactance_ohm_per_km === null
                  ? "not included (no figure)"
                  : `${formatQuantity(row.reactance_ohm_per_km, "Ω/km").display} per conductor`}
              </td>
            </tr>
            <tr data-row-key="cable_impedance">
              <th scope="row">Cable impedance at 20 °C / θ (Ω)</th>
              <td title={`${row.cable_impedance_20c_ohm ?? "—"} / ${row.cable_impedance_ohm ?? "—"}`}>
                {formatQuantity(row.cable_impedance_20c_ohm).display} /{" "}
                {formatQuantity(row.cable_impedance_ohm).display}
              </td>
              <td>√((R1 + R2)² + (X1 + X2)²)</td>
            </tr>
            <tr data-row-key="loop_impedance">
              <th scope="row">Loop impedance Zs at 20 °C / θ (Ω)</th>
              <td title={`${row.loop_impedance_20c_ohm ?? "—"} / ${row.loop_impedance_ohm ?? "—"}`}>
                {formatQuantity(row.loop_impedance_20c_ohm).display} /{" "}
                {formatQuantity(row.loop_impedance_ohm).display}
              </td>
              <td>|Ze| + |Z cable|</td>
            </tr>
            <tr data-row-key="fault_current">
              <th scope="row">Earth-fault current I_f (A)</th>
              <Quantity value={row.fault_current_a} unit="A" />
              <td>(C_min) U0 / Zs(θ)</td>
            </tr>
            <tr data-row-key="device">
              <th scope="row">Device</th>
              <td>
                {device.device_code} · {DEVICE_KIND_LABELS[device.device_kind]}
                {device.mcb_type ? ` ${MCB_TYPE_LABELS[device.mcb_type]}` : ""} ·{" "}
                {formatQuantity(device.rated_current_a, "A").display}
                {device.characteristic_text ? ` · ${device.characteristic_text}` : ""}
              </td>
              <td>{CIRCUIT_KIND_LABELS[row.circuit_kind]}</td>
            </tr>
            <tr data-row-key="disconnection_time">
              <th scope="row">Required disconnection time (s)</th>
              <Quantity value={row.required_disconnection_time_s} unit="s" />
              <td data-time-origin={row.disconnection_time_origin}>
                {TIME_ORIGIN_LABELS[row.disconnection_time_origin]}
                {row.disconnection_time_origin === "DECLARED" && device.time_source ? ` · ${device.time_source}` : ""}
              </td>
            </tr>
            <tr data-row-key="operating_current">
              <th scope="row">Operating current Ia (A)</th>
              <Quantity value={row.operating_current_a} unit="A" />
              <td data-ia-origin={row.operating_current_origin}>
                {IA_ORIGIN_LABELS[row.operating_current_origin]}
                {device.operating_current_basis
                  ? ` · ${OPERATING_CURRENT_BASIS_LABELS[device.operating_current_basis]}`
                  : ""}
                {device.operating_current_source ? ` · ${device.operating_current_source}` : ""}
              </td>
            </tr>
            <tr data-row-key="maximum_loop_impedance">
              <th scope="row">Maximum loop impedance Zs max (Ω)</th>
              <Quantity value={row.maximum_loop_impedance_ohm} unit="Ω" />
              <td data-zs-max-origin={row.maximum_loop_impedance_origin}>
                {ZS_MAX_ORIGIN_LABELS[row.maximum_loop_impedance_origin]}
                {row.register_maximum_loop_impedance ? ` · ${row.register_maximum_loop_impedance.locator}` : ""}
              </td>
            </tr>
            <tr data-row-key="margin">
              <th scope="row">Margin Zs max − Zs(θ) (Ω) · ratio I_f / Ia</th>
              <td title={`${row.loop_impedance_margin_ohm ?? "—"} · ${row.current_ratio ?? "—"}`}>
                {formatQuantity(row.loop_impedance_margin_ohm).display}
                {row.margin_percent === null ? "" : ` (${formatQuantity(row.margin_percent, "%").display})`} ·{" "}
                {formatQuantity(row.current_ratio).display}
              </td>
              <td data-row-limit-basis={row.limit_basis}>{ROW_LIMIT_BASIS_LABELS[row.limit_basis]}</td>
            </tr>
            <tr data-row-key="result">
              <th scope="row">Disconnection assured</th>
              <td data-check-result={resultKey(row.disconnection_assured)}>
                <span data-check-pill>
                  <StatusIcon tone={checkTone(resultKey(row.disconnection_assured))} size={16} />
                  {resultWord(row.disconnection_assured)}
                </span>
              </td>
              <td>Zs(θ) × Ia ≤ (C_min) U0</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function EarthFaultLoopResultPanel({ result }: EarthFaultLoopResultPanelProps) {
  return (
    <article aria-label="Earth-fault loop result">
      <header>
        <h2>Earth-fault loop result</h2>
        <p>
          Study code: <strong data-study-code="true">{result.code}</strong>
          {" - "}
          {result.name}
        </p>
        <p>
          Overall status: <strong data-result-status={result.status}>{statusHeading(result)}</strong>
        </p>
      </header>

      <BoardTable result={result} />
      <CircuitsTable rows={result.rows} />
      {result.rows.map((row) => (
        <CircuitDetail key={row.code} row={row} />
      ))}

      <section aria-label="Jurisdiction profile">
        <h3>Jurisdiction profile</h3>
        <p data-jurisdiction-profile={result.jurisdiction_profile}>
          Designed under the {result.jurisdiction_profile} profile. The loop physics carries no
          profile-dependent value; the IN profile supplies the CPWD App F tables as defaults where
          their stated scope applies, every other profile answers "limit not established" until its
          reference is verified (GAP-027).
        </p>
      </section>

      <footer>
        <p>
          Calculation results are engineering design checks, not a statutory compliance
          declaration. Confirm the governing project basis, the linked earth-fault and cable
          studies, every declared device characteristic and its source, and the required
          disconnection times before approval or issue. {METHOD_NOTE}
        </p>
      </footer>
    </article>
  );
}

export default EarthFaultLoopResultPanel;
